import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import type { ITotalReportRecord } from '@/Interfaces/Attribution/ReportRecord.interface';
import type { IWebVisitsState } from '@/Interfaces/Attribution/State.interface';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { getDefaultWebVisitsFilter } from '@/helpers/attribution';
import { mapEventsList } from '@/helpers/attribution/mapping';
import { fulfilledReportCaseHandler } from '@/helpers/beeswaxReports';
import { placementReportToControlOptions } from '@/helpers/reportingDashboard/placement.helpers';
import { pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import {
  getWebVisitsCampaignNameData,
  getWebVisitsDateData,
  getWebVisitsPixelData,
  getWebVisitsPlacementData,
  webVisitsReportMap,
} from '@/store/slices/attribution/beeswaxThunks';
import type { WebVisitsFilters } from '@/types/Attribution/filters.types';
import { createSlice } from '@reduxjs/toolkit';

const initialState: IWebVisitsState = {
  filters: getDefaultWebVisitsFilter(),

  campaigns: [],
  placementOptions: [],
  eventOptions: [],

  dateData: [],
  totals: {} as ITotalReportRecord,
  dateDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  deviceData: [],
  deviceDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  channelData: [],
  channelDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  campaignNameData: [],
  campaignNameDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  creativeData: [],
  creativeDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  publisherNameData: [],
  publisherNameDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  placementData: [],
  placementDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  dayTimeData: [],
  dayTimeDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  dayOfWeekData: [],
  dayOfWeekDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  pixelData: [],
  pixelDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  stateData: [],
  stateDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  dmaData: [],
  dmaDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  countyData: [],
  countyDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  zipData: [],
  zipDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  congressionalDistrictsData: [],
  congressionalDistrictsDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  cityData: [],
  cityDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  appNameData: [],
  appNameDataRequestId: DEFAULT_LOADING_REQUEST_ID,
};

export const webVisitsSlice = createSlice({
  name: 'webVisits',
  initialState,
  reducers: {
    setFilters: (state, { payload }: { payload: Partial<WebVisitsFilters> }) => {
      state.filters = {
        ...state.filters,
        ...payload,
      };
    },
    resetFilters: state => {
      state.filters = getDefaultWebVisitsFilter();
    },
    resetLoadingState: state => {
      Object.entries(state).forEach(([key, value]) => {
        if (key.endsWith('RequestId') && !value) {
          (state[key as keyof IWebVisitsState] as string) = DEFAULT_LOADING_REQUEST_ID;
        }
      });
    },
    resetWebVisitsPageState: () => initialState,
  },
  extraReducers: builder => {
    webVisitsReportMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key, []))
        .addCase(thunk.rejected, rejectedCaseHandler(key, []))
        .addCase(thunk.fulfilled, fulfilledReportCaseHandler(key));
    });

    builder
      .addCase(getWebVisitsDateData.pending, (state, action) => {
        const handler = pendingCaseHandler('dateData');
        state.dayOfWeekData = [];
        state.totals = {};
        handler(state, action);
      })
      .addCase(getWebVisitsDateData.rejected, (state, action) => {
        const handler = rejectedCaseHandler('dateData', []);
        state.dayOfWeekData = [];
        state.totals = {};
        handler(state, action);
      })
      .addCase(getWebVisitsDateData.fulfilled, (state, action) => {
        if (state.dateDataRequestId === action.meta.requestId) {
          state.dateDataRequestId = null;
          state.dateData = action.payload.report;
          state.totals = action.payload.totals;
        }
      })

      .addCase(getWebVisitsCampaignNameData.pending, pendingCaseHandler('campaignNameData'))
      .addCase(getWebVisitsCampaignNameData.rejected, rejectedCaseHandler('campaignNameData', []))
      .addCase(getWebVisitsCampaignNameData.fulfilled, (state, action) => {
        const handler = fulfilledReportCaseHandler('campaignNameData');
        if (state.campaignNameDataRequestId === action.meta.requestId) {
          if (state.filters.campaigns.length === 0) {
            state.campaigns = action.payload.report
              ?.filter(campaign => campaign[BeeswaxReportProperties.IP_CONVERSIONS] > 0)
              ?.map(campaign => ({
                value: +campaign[BeeswaxReportProperties.CAMPAIGN_ID],
                label: campaign[BeeswaxReportProperties.CAMPAIGN_NAME],
              }))
              .sort((a, b) => a.label.localeCompare(b.label));
          }
        }
        handler(state, action);
      })

      .addCase(getWebVisitsPlacementData.pending, pendingCaseHandler('placementData'))
      .addCase(getWebVisitsPlacementData.rejected, rejectedCaseHandler('placementData', []))
      .addCase(getWebVisitsPlacementData.fulfilled, (state, action) => {
        if (state.placementDataRequestId === action.meta.requestId) {
          const { report } = action.payload;
          const { filters } = state;
          const shouldApply = filters.campaigns.length && !filters.placement.length;

          state.placementDataRequestId = null;
          if (shouldApply) {
            state.placementOptions = placementReportToControlOptions(filters, report);
          }
          state.placementData = report;
        }
      })

      .addCase(getWebVisitsPixelData.pending, pendingCaseHandler('pixelData'))
      .addCase(getWebVisitsPixelData.rejected, rejectedCaseHandler('pixelData', []))
      .addCase(getWebVisitsPixelData.fulfilled, (state, action) => {
        const handler = fulfilledReportCaseHandler('pixelData');
        if (state.pixelDataRequestId === action.meta.requestId) {
          if (state.filters.events.length === 0) {
            state.eventOptions = mapEventsList(action.payload);
          }
        }
        handler(state, action);
      });
  },
});

export default webVisitsSlice.reducer;

export const { setFilters, resetFilters, resetLoadingState, resetWebVisitsPageState } = webVisitsSlice.actions;
